import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  Container,
  Typography,
  IconButton,
  TablePagination,
  Box,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { searchDocalls } from '../services/docallsService';

// Definizione delle colonne basata su chiamate Apache tipiche
const headCells = [
  { id: 'docall', numeric: true, disablePadding: false, label: 'ID Chiamata' },
  { id: 'REMOTE_ADDR', numeric: false, disablePadding: false, label: 'Indirizzo IP' },
  { id: 'SERVER_NAME', numeric: false, disablePadding: false, label: 'URL Chiamato' },
  { id: 'date', numeric: false, disablePadding: false, label: 'Data/Ora' },
];

function DoCallsComponent() {
  const navigate = useNavigate();
  
  // Stati per i dati e la paginazione
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Stati per l'ordinamento
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('docall');
  
  // Stati per i filtri
  const [filters, setFilters] = useState({
    docall: '',
    REMOTE_ADDR: '',
    SERVER_NAME: '',
    date: '',
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await searchDocalls({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        orderBy: orderBy,
        order: order,
        filters: filters
      });

      if (response.success) {
        setRows(response.details);
        setTotalCount(response.total);
      } else {
        setToast({ open: true, message: response.error || 'Errore durante il recupero dei dati', severity: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: 'Errore di connessione', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, orderBy, order, filters]);

  useEffect(() => {
    // Debounce per i filtri per evitare troppe chiamate API
    const timer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Torna alla prima pagina quando cambia l'ordinamento
  };

  const handleFilterChange = (event, columnId) => {
    setFilters({
      ...filters,
      [columnId]: event.target.value,
    });
    setPage(0); // Torna alla prima pagina quando cambiano i filtri
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{
        p: 2,
        mb: 4,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'white', mr: 2 }}>
            <Icons.ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', m: 0 }}>
              _docalls Management
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Totale record: {totalCount.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={fetchData} sx={{ color: 'white' }} disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : <Icons.Refresh />}
        </IconButton>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ backgroundColor: '#1e1e1e', color: 'white' }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                      sx={{
                        color: 'white !important',
                        '&.Mui-active': { color: 'primary.main !important' },
                        '& .MuiTableSortLabel-icon': {
                          color: 'rgba(255, 255, 255, 0.7) !important',
                        },
                      }}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={`${headCell.id}-filter`} sx={{ backgroundColor: '#1e1e1e', py: 1 }}>
                    <TextField
                      variant="standard"
                      size="small"
                      fullWidth
                      placeholder={`Search...`}
                      value={filters[headCell.id]}
                      onChange={(e) => handleFilterChange(e, headCell.id)}
                      sx={{
                        '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.2)' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'primary.main' },
                        input: { color: 'white', fontSize: '0.8rem' } 
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center" sx={{ py: 10 }}>
                    <CircularProgress color="primary" />
                    <Typography sx={{ mt: 2 }}>Caricamento dati in corso...</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center" sx={{ py: 10 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Nessun record trovato</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={row.docall || index} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03) !important' } }}>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{row.docall}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.REMOTE_ADDR}</TableCell>
                    <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.SERVER_NAME}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                      {row.date ? new Date(row.date).toLocaleString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: 'white',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            '.MuiTablePagination-selectIcon': { color: 'white' },
            '.MuiTablePagination-actions': { color: 'white' }
          }}
        />
      </Paper>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={6000} 
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default DoCallsComponent;
